import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight, Clock3, X } from 'lucide-react';
import { addDays, todayKST } from './periodDates';

const pad = (n) => String(n).padStart(2, '0');
const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
const smallButton = 'inline-flex size-8 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-30';
const textButton = 'rounded-full px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-30';

export function normalizePickerValue(kind, raw) {
  let text = raw.trim();
  if (!text) return '';
  if (kind === 'date') {
    if (/^\d{8}$/.test(text)) text = `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6)}`;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text) || text.startsWith('0000')) return null;
    const date = new Date(`${text}T00:00:00Z`);
    return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== text ? null : text;
  }
  if (/^\d{4}$/.test(text)) text = `${text.slice(0, 2)}:${text.slice(2)}`;
  const match = /^(\d{1,2}):(\d{2})$/.exec(text);
  return match && Number(match[1]) < 24 && Number(match[2]) < 60 ? `${pad(Number(match[1]))}:${match[2]}` : null;
}

export const pickerValueAllowed = (value, min, max) => !!value && (!min || value >= min) && (!max || value <= max);
const clampDate = (value, min, max) => (min && value < min ? min : max && value > max ? max : value);
const monthOf = (date) => date.slice(0, 7);
const moveMonth = (month, amount) => {
  const [year, number] = month.split('-').map(Number);
  const index = Math.max(0, Math.min(9999 * 12 - 1, (year - 1) * 12 + number - 1 + amount));
  return `${String(Math.floor(index / 12) + 1).padStart(4, '0')}-${pad(index % 12 + 1)}`;
};

function Calendar({ value, min, max, onPick, onClose }) {
  const today = todayKST();
  const initial = clampDate(value || today, min, max);
  const [month, setMonth] = useState(monthOf(initial));
  const [focusDate, setFocusDate] = useState(initial);
  const gridRef = useRef(null);
  const first = `${month}-01`;
  const offset = new Date(`${first}T00:00:00Z`).getUTCDay();
  const days = new Date(`${first}T00:00:00Z`);
  days.setUTCMonth(days.getUTCMonth() + 1);
  days.setUTCDate(0);
  const count = days.getUTCDate();
  const [year, monthNumber] = month.split('-');
  const setVisibleMonth = (next) => {
    const date = clampDate(`${next}-01`, min, max);
    setMonth(monthOf(date));
    setFocusDate(date);
  };
  useEffect(() => {
    gridRef.current?.querySelector(`[data-day="${focusDate}"]`)?.focus({ preventScroll: true });
  }, [focusDate]);
  const navigate = (e, date) => {
    const delta = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[e.key];
    let next;
    if (delta) next = addDays(date, delta);
    else if (e.key === 'Home') next = addDays(date, -new Date(`${date}T00:00:00Z`).getUTCDay());
    else if (e.key === 'End') next = addDays(date, 6 - new Date(`${date}T00:00:00Z`).getUTCDay());
    else if (e.key === 'PageUp' || e.key === 'PageDown') next = `${moveMonth(month, e.key === 'PageUp' ? -1 : 1)}-01`;
    else return;
    e.preventDefault();
    if (!normalizePickerValue('date', next)) return;
    next = clampDate(next, min, max);
    setMonth(monthOf(next));
    setFocusDate(next);
  };
  const visibleYear = Number(year);
  const firstYear = Math.max(1, Math.min(visibleYear, Math.max(visibleYear - 100, Number(min?.slice(0, 4) || 1))));
  const lastYear = Math.min(9999, Math.max(visibleYear, Math.min(visibleYear + 20, Number(max?.slice(0, 4) || 9999))));
  const years = Array.from({ length: lastYear - firstYear + 1 }, (_, i) => firstYear + i);
  return (
    <>
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
        <button type="button" className={smallButton} aria-label="이전 달" disabled={month === '0001-01' || (!!min && month <= monthOf(min))} onClick={() => setVisibleMonth(moveMonth(month, -1))}><ChevronLeft className="size-4" /></button>
        <div className="flex items-center gap-1 text-sm font-semibold text-gray-900" aria-live="polite">
          <select aria-label="연도" className="rounded-lg bg-transparent py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" value={year} onChange={(e) => setVisibleMonth(`${e.target.value}-${monthNumber}`)}>
            {years.map((y) => <option key={y} value={y}>{y}년</option>)}
          </select>
          <select aria-label="월" className="rounded-lg bg-transparent py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" value={monthNumber} onChange={(e) => setVisibleMonth(`${year}-${e.target.value}`)}>
            {Array.from({ length: 12 }, (_, i) => <option key={i} value={pad(i + 1)} disabled={(min && `${year}-${pad(i + 1)}` < monthOf(min)) || (max && `${year}-${pad(i + 1)}` > monthOf(max))}>{i + 1}월</option>)}
          </select>
        </div>
        <button type="button" className={smallButton} aria-label="다음 달" disabled={month === '9999-12' || (!!max && month >= monthOf(max))} onClick={() => setVisibleMonth(moveMonth(month, 1))}><ChevronRight className="size-4" /></button>
      </div>
      <div ref={gridRef} role="grid" aria-label={`${year}년 ${Number(monthNumber)}월`} className="p-3">
        <div role="row" className="mb-1 grid grid-cols-7">
          {weekdays.map((d, i) => <span role="columnheader" key={d} className={`py-1 text-center text-[11px] ${i === 0 ? 'text-red-400' : 'text-gray-400'}`}>{d}</span>)}
        </div>
        {Array.from({ length: 6 }, (_, row) => (
          <div role="row" key={row} className="grid grid-cols-7">
            {Array.from({ length: 7 }, (_, col) => {
              const day = row * 7 + col - offset + 1;
              if (day < 1 || day > count) return <span role="gridcell" key={col} className="h-9" />;
              const date = `${month}-${pad(day)}`;
              const selected = date === value;
              return (
                <span role="gridcell" aria-selected={selected} key={col} className="flex justify-center">
                  <button type="button" data-day={date} tabIndex={focusDate === date ? 0 : -1} aria-label={`${year}년 ${Number(monthNumber)}월 ${day}일 ${weekdays[col]}요일`} aria-current={date === today ? 'date' : undefined}
                    disabled={!pickerValueAllowed(date, min, max)} onFocus={() => setFocusDate(date)} onKeyDown={(e) => navigate(e, date)} onClick={() => onPick(date)}
                    className={`my-0.5 size-8 rounded-full text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:text-gray-300 ${selected ? 'bg-primary font-semibold text-white hover:bg-primary-hover' : `hover:bg-primary/10 ${col === 0 ? 'text-red-500' : 'text-gray-700'} ${date === today ? 'ring-1 ring-inset ring-primary' : ''}`}`}>
                    {day}
                  </button>
                </span>
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2">
        <button type="button" className={textButton} onClick={() => onPick('')}>비우기</button>
        <button type="button" className={textButton} disabled={!pickerValueAllowed(today, min, max)} onClick={() => onPick(today)}>오늘</button>
        <button type="button" className={textButton} onClick={onClose}>닫기</button>
      </div>
    </>
  );
}

function TimeList({ label, count, value, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    const list = ref.current;
    const selected = list?.querySelector('[aria-selected="true"]');
    if (selected) list.scrollTop = Math.max(0, selected.offsetTop - (list.clientHeight - selected.offsetHeight) / 2);
  }, [value]);
  return (
    <div className="min-w-0 flex-1">
      <p className="mb-2 text-center text-[11px] font-medium text-gray-400">{label}</p>
      <div ref={ref} role="listbox" aria-label={label} className="relative h-40 space-y-1 overflow-y-auto overscroll-contain rounded-xl bg-gray-50 p-1 [scrollbar-width:thin]">
        {Array.from({ length: count }, (_, n) => (
          <button key={n} type="button" role="option" aria-selected={n === value} tabIndex={n === value ? 0 : -1} onClick={() => onChange(n)}
            onKeyDown={(e) => {
              if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) return;
              e.preventDefault();
              const next = e.key === 'Home' ? 0 : e.key === 'End' ? count - 1 : Math.max(0, Math.min(count - 1, n + (e.key === 'ArrowUp' ? -1 : 1)));
              onChange(next);
              ref.current.children[next]?.focus({ preventScroll: true });
            }}
            className={`block min-h-8 w-full rounded-lg text-center text-sm tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${n === value ? 'bg-primary font-semibold text-white' : 'text-gray-600 hover:bg-gray-200'}`}>
            {pad(n)}
          </button>
        ))}
      </div>
    </div>
  );
}

function TimePanel({ value, min, max, onPick, onClose }) {
  const [hour, setHour] = useState(Number(value?.slice(0, 2) || 9));
  const [minute, setMinute] = useState(Number(value?.slice(3, 5) || 0));
  const time = `${pad(hour)}:${pad(minute)}`;
  const ref = useRef(null);
  useEffect(() => { ref.current?.querySelector('[aria-selected="true"]')?.focus({ preventScroll: true }); }, []);
  return (
    <div ref={ref}>
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div><p className="text-[11px] text-gray-500">24시간 형식</p><p className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900">{time}</p></div>
        <button type="button" className={smallButton} aria-label="시간 선택 닫기" onClick={onClose}><X className="size-4" /></button>
      </div>
      <div className="flex gap-3 p-4"><TimeList label="시" count={24} value={hour} onChange={setHour} /><TimeList label="분" count={60} value={minute} onChange={setMinute} /></div>
      <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2">
        <button type="button" className={textButton} onClick={() => onPick('')}>비우기</button>
        <button type="button" disabled={!pickerValueAllowed(time, min, max)} onClick={() => onPick(time)} className="rounded-full bg-primary px-4 py-1.5 text-xs font-medium text-white hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 disabled:opacity-30">적용</button>
      </div>
    </div>
  );
}

/** 모달 안에서는 같은 DOM 안에 팝업을 둔다. 공용 Modal의 초점 이동·스크롤 영역과 충돌하지 않는다. */
export function DateTimeInput({ kind, value = '', onChange, min, max, disabled, className, ...props }) {
  const [draft, setDraft] = useState(value || '');
  const [touched, setTouched] = useState(false);
  const [host, setHost] = useState(null);
  const [position, setPosition] = useState(null);
  const inputRef = useRef(null);
  const fieldRef = useRef(null);
  const popupRef = useRef(null);
  const emitted = useRef(value || '');
  const popupId = useId();
  const parsed = normalizePickerValue(kind, draft);
  const invalid = touched && !!draft && (!parsed || !pickerValueAllowed(parsed, min, max));
  const errorMessage = min && parsed && parsed < min ? `${min}부터 입력할 수 있습니다.` : max && parsed && parsed > max ? `${max}까지 입력할 수 있습니다.` : kind === 'date' ? '날짜를 YYYY-MM-DD 형식으로 입력하세요.' : '시간을 HH:MM 형식으로 입력하세요.';
  const Icon = kind === 'date' ? CalendarDays : Clock3;
  const open = !!host;
  const ready = !!position;
  useEffect(() => {
    if (open && ready) popupRef.current?.querySelector('[data-day][tabindex="0"], [role="option"][aria-selected="true"]')?.focus({ preventScroll: true });
  }, [open, ready]);
  useEffect(() => {
    if ((value || '') !== emitted.current) { setDraft(value || ''); emitted.current = value || ''; setTouched(false); }
  }, [value]);
  useEffect(() => {
    inputRef.current?.setCustomValidity(draft && (!parsed || !pickerValueAllowed(parsed, min, max)) ? errorMessage : '');
  }, [draft, parsed, min, max, errorMessage]);
  const commit = (next) => { emitted.current = next; onChange(next); };
  const close = (restore = false) => { setHost(null); setPosition(null); if (restore) inputRef.current?.focus({ preventScroll: true }); };
  const pick = (next) => { setDraft(next); setTouched(false); commit(next); close(true); };
  const toggle = () => {
    if (open) { close(true); return; }
    if (!disabled) setHost(inputRef.current.closest('[role="dialog"]') || document.body);
  };
  useLayoutEffect(() => {
    if (!open || !popupRef.current) return undefined;
    const place = () => {
      const anchor = fieldRef.current.getBoundingClientRect();
      const popup = popupRef.current.getBoundingClientRect();
      const mobile = window.innerWidth < 768;
      const topEdge = mobile ? 58 : 8;
      const bottomEdge = window.innerHeight - (mobile ? 76 : 8);
      const height = Math.min(popup.height, Math.max(120, bottomEdge - topEdge));
      const left = Math.max(8, Math.min(anchor.left, window.innerWidth - popup.width - 8));
      const top = Math.max(topEdge, Math.min(anchor.bottom + height + 6 <= bottomEdge ? anchor.bottom + 6 : anchor.top - height - 6, bottomEdge - height));
      const origin = host === document.body ? { left: 0, top: 0 } : host.getBoundingClientRect();
      setPosition({ left: left - origin.left, top: top - origin.top, maxHeight: bottomEdge - topEdge });
    };
    place();
    const scroll = (e) => { if (!popupRef.current?.contains(e.target)) place(); };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', scroll, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', scroll, true); };
  }, [host, open]);
  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => {
      if (!fieldRef.current?.contains(e.target) && !popupRef.current?.contains(e.target)) close();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('focusin', outside);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('focusin', outside); };
  }, [open]);
  useEffect(() => { if (disabled && open) close(); }, [disabled, open]);
  const escape = (e) => {
    if (e.key === 'Escape' && open) { e.preventDefault(); e.stopPropagation(); close(true); }
  };
  const panelKeyDown = (e) => {
    escape(e);
    if (e.key !== 'Tab') return;
    const controls = [...popupRef.current.querySelectorAll('button, input, select')].filter((el) => !el.disabled && el.tabIndex >= 0);
    if ((!e.shiftKey && e.target === controls[controls.length - 1]) || (e.shiftKey && e.target === controls[0])) {
      e.preventDefault();
      e.stopPropagation();
      const nextControls = [...host.querySelectorAll('button, input:not([type="hidden"]), select, textarea, [href]')]
        .filter((el) => !popupRef.current.contains(el) && !el.disabled && el.tabIndex >= 0 && el.getClientRects().length);
      const trigger = fieldRef.current.querySelector('button');
      const next = e.shiftKey ? inputRef.current : nextControls[nextControls.indexOf(trigger) + 1] || inputRef.current;
      close();
      next?.focus({ preventScroll: true });
    }
  };
  const panel = open ? createPortal(
    <div ref={popupRef} id={popupId} data-academy-picker={kind} role="dialog" aria-label={kind === 'date' ? '날짜 선택' : '시간 선택'} onKeyDown={panelKeyDown}
      className="w-72 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-xl"
      style={{ position: host === document.body ? 'fixed' : 'absolute', zIndex: 9999981, left: 0, top: 0, visibility: position ? 'visible' : 'hidden', ...position }}>
      {kind === 'date' ? <Calendar value={value} min={min} max={max} onPick={pick} onClose={() => close(true)} /> : <TimePanel value={value} min={min} max={max} onPick={pick} onClose={() => close(true)} />}
    </div>, host,
  ) : null;
  return (
    <>
      <span ref={fieldRef} className={`academy-picker-field relative flex items-center gap-1 ${className}`} data-invalid={invalid || undefined}>
        <input {...props} ref={inputRef} type="text" data-input-type={kind} value={draft} min={min} max={max} disabled={disabled} autoComplete="off" inputMode="numeric"
          placeholder={props.placeholder || (kind === 'date' ? '날짜 선택' : '시간 선택')}
          aria-invalid={invalid || undefined} aria-describedby={invalid ? `${popupId}-error` : props['aria-describedby']}
          className="min-w-0 flex-1 bg-transparent p-0 tabular-nums text-inherit outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
          onChange={(e) => {
            const text = e.target.value;
            setDraft(text);
            const next = normalizePickerValue(kind, text);
            commit(next === '' ? '' : next && pickerValueAllowed(next, min, max) ? next : '');
          }}
          onBlur={() => { setTouched(true); if (parsed && pickerValueAllowed(parsed, min, max)) setDraft(parsed); }}
          onInvalid={(e) => { e.preventDefault(); setTouched(true); }}
          onKeyDown={(e) => {
            escape(e);
            if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); if (!open) toggle(); }
            if (e.key === 'Enter') { e.preventDefault(); setTouched(true); if (parsed && pickerValueAllowed(parsed, min, max)) setDraft(parsed); }
          }} />
        <button type="button" disabled={disabled} aria-label={kind === 'date' ? '달력 열기' : '시간 선택 열기'} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? popupId : undefined} onClick={toggle}
          className="-mr-1 inline-flex size-6 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-primary/10 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40"><Icon className="size-4" aria-hidden="true" /></button>
      </span>
      {invalid && <span id={`${popupId}-error`} role="status" className="mt-1 block text-xs text-danger">{errorMessage}</span>}
      {panel}
    </>
  );
}
